import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, Save, X } from 'lucide-react';
import axios from 'axios';
import FileUploader from '../../components/FileUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CourseManagement = () => {
  const [courses, setCourses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: '',
    titleArabic: '',
    description: '',
    descriptionArabic: '',
    duration: '',
    durationArabic: '',
    credits: '',
    creditsArabic: '',
    category: '',
    location: '',
    locationArabic: '',
    image: '',
    videoUrl: '',
    brochures: [],
    status: 'active',
    notes: '',
    notesArabic: '',
    completedDate: '',
    completedDateArabic: '',
    plannedDate: '',
    plannedDateArabic: ''
  });

  const [brochureForm, setBrochureForm] = useState({ title: '', url: '' });

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchCourses(token);
  }, [navigate]);

  const fetchCourses = async (token) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data.courses || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
      if (error.response?.status === 401) {
        navigate('/admin');
      }
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const token = localStorage.getItem('adminToken');

    try {
      if (editingCourse) {
        await axios.put(`${BACKEND_URL}/api/admin/courses/${editingCourse.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/courses`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      setShowForm(false);
      setEditingCourse(null);
      resetForm();
      fetchCourses(token);
      alert(editingCourse ? 'Course updated successfully!' : 'Course created successfully!');
    } catch (error) {
      console.error('Error saving course:', error);
      alert('Error saving course. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (course) => {
    setEditingCourse(course);
    setFormData({
      title: course.title || '',
      titleArabic: course.titleArabic || '',
      description: course.description || '',
      descriptionArabic: course.descriptionArabic || '',
      duration: course.duration || '',
      durationArabic: course.durationArabic || '',
      credits: course.credits || '',
      creditsArabic: course.creditsArabic || '',
      category: course.category || '',
      location: course.location || '',
      locationArabic: course.locationArabic || '',
      image: course.image || '',
      videoUrl: course.videoUrl || '',
      brochures: course.brochures || [],
      status: course.status || 'active',
      notes: course.notes || '',
      notesArabic: course.notesArabic || '',
      completedDate: course.completedDate || '',
      completedDateArabic: course.completedDateArabic || '',
      plannedDate: course.plannedDate || '',
      plannedDateArabic: course.plannedDateArabic || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;

    const token = localStorage.getItem('adminToken');
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/courses/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCourses(token);
      alert('Course deleted successfully!');
    } catch (error) {
      console.error('Error deleting course:', error);
      alert('Error deleting course. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '', titleArabic: '', description: '', descriptionArabic: '',
      duration: '', durationArabic: '', credits: '', creditsArabic: '',
      category: '', location: '', locationArabic: '', image: '',
      videoUrl: '', brochures: [], status: 'active', notes: '', notesArabic: '',
      completedDate: '', completedDateArabic: '', plannedDate: '', plannedDateArabic: ''
    });
    setBrochureForm({ title: '', url: '' });
  };

  const handleAddBrochure = () => {
    if (brochureForm.title && brochureForm.url) {
      setFormData({
        ...formData,
        brochures: [...formData.brochures, { title: brochureForm.title, url: brochureForm.url }]
      });
      setBrochureForm({ title: '', url: '' });
    }
  };

  const handleRemoveBrochure = (index) => {
    setFormData({
      ...formData,
      brochures: formData.brochures.filter((_, i) => i !== index)
    });
  };

  const handlePdfUpload = async (file) => {
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/upload/pdf`, formDataUpload);
      return response.data.url;
    } catch (error) {
      console.error('Error uploading PDF:', error);
      alert('Error uploading PDF');
      return null;
    }
  };

  const groupedCourses = {
    previous: courses.filter(c => c.status === 'previous'),
    active: courses.filter(c => c.status === 'active'),
    future: courses.filter(c => c.status === 'future')
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="bg-white shadow-md">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin/dashboard" className="text-slate-600 hover:text-slate-800">
                <ArrowLeft size={24} />
              </Link>
              <h1 className="text-2xl font-bold text-slate-800">Course Management</h1>
            </div>
            <button
              onClick={() => { setShowForm(true); setEditingCourse(null); resetForm(); }}
              className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-lg"
            >
              <Plus size={20} />
              Add New Course
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-8">
        {showForm ? (
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">{editingCourse ? 'Edit Course' : 'Add New Course'}</h2>
              <button onClick={() => { setShowForm(false); setEditingCourse(null); }} className="text-slate-500 hover:text-slate-700">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-1">Title (English) *</label>
                  <input type="text" name="title" value={formData.title} onChange={handleInputChange} required className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">Title (Arabic) *</label>
                  <input type="text" name="titleArabic" value={formData.titleArabic} onChange={handleInputChange} required className="w-full px-3 py-2 border rounded-lg" dir="rtl" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-1">Description (English) *</label>
                  <textarea name="description" value={formData.description} onChange={handleInputChange} required rows="4" className="w-full px-3 py-2 border rounded-lg"></textarea>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">Description (Arabic) *</label>
                  <textarea name="descriptionArabic" value={formData.descriptionArabic} onChange={handleInputChange} required rows="4" className="w-full px-3 py-2 border rounded-lg" dir="rtl"></textarea>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-1">Duration *</label>
                  <input type="text" name="duration" value={formData.duration} onChange={handleInputChange} required className="w-full px-3 py-2 border rounded-lg" placeholder="4 Days" />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">Duration (Arabic) *</label>
                  <input type="text" name="durationArabic" value={formData.durationArabic} onChange={handleInputChange} required className="w-full px-3 py-2 border rounded-lg" dir="rtl" placeholder="4 أيام" />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">Category *</label>
                  <input type="text" name="category" value={formData.category} onChange={handleInputChange} required className="w-full px-3 py-2 border rounded-lg" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-1">Credits *</label>
                  <input type="text" name="credits" value={formData.credits} onChange={handleInputChange} required className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">Credits (Arabic) *</label>
                  <input type="text" name="creditsArabic" value={formData.creditsArabic} onChange={handleInputChange} required className="w-full px-3 py-2 border rounded-lg" dir="rtl" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-1">Location *</label>
                  <input type="text" name="location" value={formData.location} onChange={handleInputChange} required className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">Location (Arabic) *</label>
                  <input type="text" name="locationArabic" value={formData.locationArabic} onChange={handleInputChange} required className="w-full px-3 py-2 border rounded-lg" dir="rtl" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <FileUploader
                    type="image"
                    label="Course Image"
                    currentUrl={formData.image}
                    onUploadSuccess={(url) => setFormData({ ...formData, image: url })}
                  />
                  <input 
                    type="text" 
                    name="image" 
                    value={formData.image} 
                    onChange={handleInputChange} 
                    className="w-full px-3 py-2 border rounded-lg mt-2" 
                    placeholder="Or paste image URL here"
                  />
                </div>
                <div>
                  <FileUploader
                    type="video"
                    label="Course Video"
                    currentUrl={formData.videoUrl}
                    onUploadSuccess={(url) => setFormData({ ...formData, videoUrl: url })}
                  />
                  <input 
                    type="text" 
                    name="videoUrl" 
                    value={formData.videoUrl} 
                    onChange={handleInputChange} 
                    className="w-full px-3 py-2 border rounded-lg mt-2" 
                    placeholder="Or paste video URL here"
                  />
                </div>
              </div>

              {/* PDF Brochures Section */}
              <div className="bg-slate-50 p-4 rounded-lg">
                <label className="block text-sm font-semibold mb-3">PDF Brochures</label>
                
                {/* Existing Brochures */}
                {formData.brochures.length > 0 && (
                  <div className="mb-4 space-y-2">
                    {formData.brochures.map((brochure, index) => (
                      <div key={index} className="flex items-center justify-between bg-white p-3 rounded border">
                        <div>
                          <span className="font-medium text-sm">{brochure.title}</span>
                          <a href={brochure.url} target="_blank" rel="noopener noreferrer" className="text-xs text-cyan-600 ml-3">View PDF</a>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveBrochure(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add New Brochure */}
                <div className="grid md:grid-cols-3 gap-3">
                  <input
                    type="text"
                    placeholder="Brochure Title (e.g., Course Outline)"
                    value={brochureForm.title}
                    onChange={(e) => setBrochureForm(prev => ({ ...prev, title: e.target.value }))}
                    className="px-3 py-2 border rounded-lg"
                  />
                  <div className="relative">
                    <input
                      type="file"
                      accept="application/pdf"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const url = await handlePdfUpload(file);
                          if (url) {
                            setBrochureForm(prev => ({ ...prev, url }));
                          }
                        }
                      }}
                      className="px-3 py-2 border rounded-lg w-full text-sm"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleAddBrochure}
                    disabled={!brochureForm.title || !brochureForm.url}
                    className="bg-cyan-500 text-white px-4 py-2 rounded-lg hover:bg-cyan-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-sm font-medium"
                  >
                    + Add Brochure
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-2">Upload PDF brochures (max 10MB each)</p>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1">Status *</label>
                <select name="status" value={formData.status} onChange={handleInputChange} required className="w-full px-3 py-2 border rounded-lg">
                  <option value="previous">Previous (Completed)</option>
                  <option value="active">Active (Current)</option>
                  <option value="future">Future (Upcoming)</option>
                </select>
              </div>

              {formData.status === 'previous' && (
                <div className="grid md:grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg">
                  <div>
                    <label className="block text-sm font-semibold mb-1">Completed Date</label>
                    <input type="text" name="completedDate" value={formData.completedDate} onChange={handleInputChange} className="w-full px-3 py-2 border rounded-lg" placeholder="December 2024" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1">Completed Date (Arabic)</label>
                    <input type="text" name="completedDateArabic" value={formData.completedDateArabic} onChange={handleInputChange} className="w-full px-3 py-2 border rounded-lg" dir="rtl" placeholder="ديسمبر 2024" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold mb-1">Notes (English)</label>
                    <textarea name="notes" value={formData.notes} onChange={handleInputChange} rows="2" className="w-full px-3 py-2 border rounded-lg" placeholder="Successfully completed with..."></textarea>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold mb-1">Notes (Arabic)</label>
                    <textarea name="notesArabic" value={formData.notesArabic} onChange={handleInputChange} rows="2" className="w-full px-3 py-2 border rounded-lg" dir="rtl" placeholder="تم إكماله بنجاح..."></textarea>
                  </div>
                </div>
              )}

              {formData.status === 'future' && (
                <div className="grid md:grid-cols-2 gap-4 bg-orange-50 p-4 rounded-lg">
                  <div>
                    <label className="block text-sm font-semibold mb-1">Planned Date</label>
                    <input type="text" name="plannedDate" value={formData.plannedDate} onChange={handleInputChange} className="w-full px-3 py-2 border rounded-lg" placeholder="April 2026" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1">Planned Date (Arabic)</label>
                    <input type="text" name="plannedDateArabic" value={formData.plannedDateArabic} onChange={handleInputChange} className="w-full px-3 py-2 border rounded-lg" dir="rtl" placeholder="أبريل 2026" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold mb-1">Notes (English)</label>
                    <textarea name="notes" value={formData.notes} onChange={handleInputChange} rows="2" className="w-full px-3 py-2 border rounded-lg" placeholder="Early bird discount..."></textarea>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold mb-1">Notes (Arabic)</label>
                    <textarea name="notesArabic" value={formData.notesArabic} onChange={handleInputChange} rows="2" className="w-full px-3 py-2 border rounded-lg" dir="rtl" placeholder="خصم الحجز المبكر..."></textarea>
                  </div>
                </div>
              )}

              <div className="flex gap-4 pt-4">
                <button type="submit" disabled={loading} className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-2 rounded-lg disabled:opacity-50">
                  <Save size={20} />
                  {loading ? 'Saving...' : (editingCourse ? 'Update Course' : 'Create Course')}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setEditingCourse(null); }} className="px-6 py-2 border border-slate-300 rounded-lg hover:bg-slate-50">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Active Courses */}
            <div>
              <h2 className="text-xl font-bold mb-4 text-green-700">Active Courses ({groupedCourses.active.length})</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {groupedCourses.active.map(course => (
                  <div key={course.id} className="bg-white rounded-lg shadow-md p-4 border-l-4 border-green-500">
                    <h3 className="font-bold text-lg mb-2">{course.title}</h3>
                    <p className="text-sm text-slate-600 mb-3">{course.category} • {course.duration}</p>
                    <div className="flex gap-2">
                      <button onClick={() => handleEdit(course)} className="flex items-center gap-1 text-cyan-600 hover:text-cyan-700">
                        <Edit size={16} /> Edit
                      </button>
                      <button onClick={() => handleDelete(course.id)} className="flex items-center gap-1 text-red-600 hover:text-red-700">
                        <Trash2 size={16} /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Previous Courses */}
            <div>
              <h2 className="text-xl font-bold mb-4 text-slate-700">Previous Courses ({groupedCourses.previous.length})</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {groupedCourses.previous.map(course => (
                  <div key={course.id} className="bg-white rounded-lg shadow-md p-4 border-l-4 border-slate-400">
                    <h3 className="font-bold text-lg mb-2">{course.title}</h3>
                    <p className="text-sm text-slate-600 mb-3">{course.completedDate}</p>
                    <div className="flex gap-2">
                      <button onClick={() => handleEdit(course)} className="flex items-center gap-1 text-cyan-600 hover:text-cyan-700">
                        <Edit size={16} /> Edit
                      </button>
                      <button onClick={() => handleDelete(course.id)} className="flex items-center gap-1 text-red-600 hover:text-red-700">
                        <Trash2 size={16} /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Future Courses */}
            <div>
              <h2 className="text-xl font-bold mb-4 text-orange-700">Future Courses ({groupedCourses.future.length})</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {groupedCourses.future.map(course => (
                  <div key={course.id} className="bg-white rounded-lg shadow-md p-4 border-l-4 border-orange-500">
                    <h3 className="font-bold text-lg mb-2">{course.title}</h3>
                    <p className="text-sm text-slate-600 mb-3">{course.plannedDate}</p>
                    <div className="flex gap-2">
                      <button onClick={() => handleEdit(course)} className="flex items-center gap-1 text-cyan-600 hover:text-cyan-700">
                        <Edit size={16} /> Edit
                      </button>
                      <button onClick={() => handleDelete(course.id)} className="flex items-center gap-1 text-red-600 hover:text-red-700">
                        <Trash2 size={16} /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseManagement;
